import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeftIcon, 
  PaperClipIcon,
  UserIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ChatBubbleLeftIcon
} from '@heroicons/react/24/outline';
import ticketService from '../../services/ticketService';
import AdminTicketActions from '../../components/tickets/AdminTicketActions';
import ChatRoom from '../chat/ChatRoom';

const TicketDetail = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const ticketId = params.ticketId || params.id || params.ticket_id;
  
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [userPermissions, setUserPermissions] = useState({});
  const [showChat, setShowChat] = useState(false);

  useEffect(() => {
    if (ticketId) {
      fetchTicket();
    } else {
      setError('No ticket ID found in URL. Please check the URL and try again.');
      setLoading(false);
    }
  }, [ticketId]);

  const fetchTicket = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await ticketService.getTicket(ticketId);
      console.log('Ticket API Response:', response);
      
      // FIXED: Handle response structure properly
      let ticketData = null;
      let commentsData = [];
      let permissionsData = {};
      
      if (response?.data?.ticket) {
        ticketData = response.data.ticket;
        commentsData = response.data.ticket.comments || [];
        permissionsData = response.data.user_context || {};
      } else if (response?.ticket) {
        ticketData = response.ticket;
        commentsData = response.comments || [];
        permissionsData = response.user_context || {};
      } else if (response?.data && response.data.id) {
        ticketData = response.data;
        commentsData = response.data.comments || [];
        permissionsData = response.user_context || {};
      }
      
      if (!ticketData || !ticketData.id) {
        throw new Error('No valid ticket data received');
      }
      
      setTicket(ticketData);
      setComments(commentsData);
      setUserPermissions(permissionsData);
      
      // FIXED: Store the user context from backend in ticketService
      if (permissionsData && Object.keys(permissionsData).length > 0) {
        console.log('Storing user context from ticket API:', permissionsData);
        ticketService.storeUserContext({
          ...permissionsData,
          fetched_at: Date.now(),
          source: 'ticket_detail_api'
        });
      }
      
    } catch (err) {
      console.error('Fetch ticket error:', err);
      setError(err.message || 'Failed to load ticket');
      setTicket(null);
      setComments([]);
      setUserPermissions({});
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    
    if (!newComment.trim()) {
      alert('Please enter a comment');
      return;
    }

    try {
      setSubmittingComment(true);
      
      await ticketService.addComment(
        ticketId, 
        newComment.trim(), 
        isPrivate, 
        attachments
      );
      
      setNewComment('');
      setIsPrivate(false);
      setAttachments([]);
      
      await fetchTicket();
      
    } catch (err) {
      console.error('Error adding comment:', err);
      alert(err.message || 'Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    
    files.forEach(file => {
      const validation = ticketService.validateFile(file);
      if (validation.isValid) {
        validFiles.push(file);
      } else {
        alert(`${file.name}: ${validation.errors.join(', ')}`);
      }
    });
    
    setAttachments(prev => [...prev, ...validFiles]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleAdminAction = async (action, data = null) => {
    console.log('Admin action triggered:', action, data);
    
    try {
      switch (action) {
        case 'ticket_updated':
        case 'start_working':
        case 'resolved':
        case 'closed':
        case 'assigned':
        case 'escalated':
        case 'forwarded':
          // Refresh ticket data after any status/assignment change
          await fetchTicket();
          break;
          
        case 'add_comment':
          document.getElementById('comment-section')?.scrollIntoView({ behavior: 'smooth' });
          break;
          
        case 'open_chat':
          setShowChat(true);
          break;
          
        default:
          console.log('Unhandled action:', action);
      }
    } catch (error) {
      console.error('Error handling admin action:', error);
      alert(error.message || 'Action failed');
    }
  };

  const formatStatus = (status) => {
    return ticketService.formatStatus(status);
  };

  const formatPriority = (priority) => {
    return ticketService.formatPriority(priority);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    
    try {
      return new Date(dateString).toLocaleString();
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  // FIXED: Check chat availability using sessionStorage
  const canShowChat = () => {
    if (!ticket || !ticket.agent_id) return false;
    
    // Try sessionStorage first, then localStorage
    const sessionUser = JSON.parse(sessionStorage.getItem('user') || '{}');
    const localUser = JSON.parse(localStorage.getItem('user') || '{}');
    const user = sessionUser.id ? sessionUser : localUser;
    
    return ticket.creator_id === user.id || ticket.agent_id === user.id;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading ticket details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Ticket</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="space-x-4">
            <button
              onClick={() => fetchTicket()}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/tickets')}
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
            >
              Back to Tickets
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">No ticket data found</p>
          <button
            onClick={() => navigate('/tickets')}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Back to Tickets
          </button>
        </div>
      </div>
    );
  }

  const statusInfo = formatStatus(ticket.status);
  const priorityInfo = formatPriority(ticket.priority);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Chat Modal */}
      {showChat && canShowChat() && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-5/6 flex flex-col">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">
                Support Chat - Ticket {ticket.custom_id || ticket.display_id || `#${ticket.id}`}
              </h3>
              <button
                onClick={() => setShowChat(false)}
                className="text-gray-400 hover:text-gray-600 p-2 rounded"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <ChatRoom
                ticketId={ticket.id}
                user={JSON.parse(sessionStorage.getItem('user') || localStorage.getItem('user') || '{}')}
                onClose={() => setShowChat(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/tickets')}
                className="mr-4 p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
              <h1 className="text-xl font-semibold text-gray-900">
                Ticket {ticket.custom_id || ticket.display_id || `#${ticket.id}`}
              </h1>
            </div>
            
            <div className="flex items-center space-x-3">
              {canShowChat() && (
                <button
                  onClick={() => setShowChat(true)}
                  className="inline-flex items-center px-3 py-1.5 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100"
                >
                  <ChatBubbleLeftIcon className="h-4 w-4 mr-2" />
                  Chat
                </button>
              )}
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityInfo.bgColor} ${priorityInfo.textColor}`}>
                {priorityInfo.label}
              </span>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.bgColor} ${statusInfo.textColor}`}>
                {statusInfo.label}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Ticket Details */}
            <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {ticket.title || 'No Title'}
              </h2>
              
              <div className="prose max-w-none text-gray-700 mb-6">
                <p className="whitespace-pre-wrap">{ticket.description || 'No description provided'}</p>
              </div>

              {/* Ticket Meta Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                <div className="flex items-center">
                  <UserIcon className="h-4 w-4 mr-2" />
                  <span>Created by: {ticket.creator?.name || 'Unknown'}</span>
                </div>
                <div className="flex items-center">
                  <ClockIcon className="h-4 w-4 mr-2" />
                  <span>Created: {formatDate(ticket.created_at)}</span>
                </div>
                {ticket.category && (
                  <div className="flex items-center">
                    <span>Category: {ticket.category.name || 'Uncategorized'}</span>
                  </div>
                )}
                {ticket.subcategory && (
                  <div className="flex items-center">
                    <span>Subcategory: {ticket.subcategory.name}</span>
                  </div>
                )}
                {ticket.agent && (
                  <div className="flex items-center">
                    <UserIcon className="h-4 w-4 mr-2" />
                    <span>Assigned to: {ticket.agent.name}</span>
                  </div>
                )}
              </div>

              {/* Attachments */}
              {ticket.attachments && ticket.attachments.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Attachments</h3>
                  <div className="space-y-2">
                    {ticket.attachments.map((attachment, index) => (
                      <div key={index} className="flex items-center p-3 bg-gray-50 rounded-md border">
                        <PaperClipIcon className="h-4 w-4 text-gray-400 mr-3" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {attachment.name || `Attachment ${index + 1}`}
                          </p>
                          <p className="text-xs text-gray-500">
                            {attachment.size ? ticketService.formatFileSize(attachment.size) : 'Unknown size'}
                            {attachment.type && ` • ${attachment.type}`}
                            {attachment.uploaded_at && ` • ${new Date(attachment.uploaded_at).toLocaleDateString()}`}
                          </p>
                        </div>
                        {attachment.path && (
                          <button
                            onClick={async (e) => {
                              try {
                                const downloadBtn = e.currentTarget;
                                const originalText = downloadBtn.textContent;
                                downloadBtn.textContent = 'Downloading...';
                                downloadBtn.disabled = true;
                                
                                const response = await fetch(
                                  `${process.env.REACT_APP_API_URL || '/api'}/tickets/${ticket.id}/download?attachment=${index}`,
                                  {
                                    headers: {
                                      'Authorization': `Bearer ${sessionStorage.getItem('access_token')}`,
                                      'Accept': 'application/octet-stream'
                                    }
                                  }
                                );
                                
                                if (!response.ok) {
                                  throw new Error('Download failed');
                                }
                                
                                const blob = await response.blob();
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = attachment.name || 'download';
                                
                                document.body.appendChild(link);
                                link.click();
                                
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);
                                
                                downloadBtn.textContent = originalText;
                                downloadBtn.disabled = false;
                                
                              } catch (error) {
                                console.error('Download failed:', error);
                                alert('Failed to download file. Please try again.');
                                
                                const downloadBtn = e.currentTarget;
                                downloadBtn.textContent = 'Download';
                                downloadBtn.disabled = false;
                              }
                            }}
                            className="ml-2 inline-flex items-center px-3 py-1 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Download
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Comments Section */}
            <div id="comment-section" className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Comments ({comments.length})
              </h3>

              {/* Comments List */}
              <div className="space-y-4 mb-6">
                {comments.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No comments yet</p>
                ) : (
                  comments.map((comment, index) => (
                    <div key={comment.id || index} className="border-l-4 border-blue-200 pl-4 py-2">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          <span className="font-medium text-gray-900">
                            {comment.author?.name || 'Unknown User'}
                          </span>
                          {comment.author?.is_agent && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              Agent
                            </span>
                          )}
                          {comment.is_private && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                              Private
                            </span>
                          )}
                        </div>
                        <span className="text-sm text-gray-500">
                          {formatDate(comment.created_at)}
                        </span>
                      </div>
                      <p className="text-gray-700 whitespace-pre-wrap">{comment.content}</p>
                    </div>
                  ))
                )}
              </div>

              {/* Add Comment Form */}
              {userPermissions.can_comment && (
                <form onSubmit={handleAddComment} className="border-t border-gray-200 pt-6">
                  <div className="mb-4">
                    <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-2">
                      Add Comment
                    </label>
                    <textarea
                      id="comment"
                      rows={4}
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter your comment..."
                      disabled={submittingComment}
                    />
                  </div>

                  {/* Comment Options */}
                  {userPermissions.is_agent && (
                    <div className="mb-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={isPrivate}
                          onChange={(e) => setIsPrivate(e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          disabled={submittingComment}
                        />
                        <span className="ml-2 text-sm text-gray-700">Private comment (visible to agents only)</span>
                      </label>
                    </div>
                  )}

                  {/* File Attachments */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Attachments (optional)
                    </label>
                    <input
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.txt,.csv"
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      disabled={submittingComment}
                    />
                    
                    {/* Selected Files */}
                    {attachments.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {attachments.map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                            <span className="text-sm text-gray-700">{file.name}</span>
                            <button
                              type="button"
                              onClick={() => removeAttachment(index)}
                              className="text-red-500 hover:text-red-700 text-sm"
                              disabled={submittingComment}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={submittingComment || !newComment.trim()}
                      className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submittingComment ? 'Adding...' : 'Add Comment'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Sidebar - Admin Actions */}
          <div className="space-y-6">
            <AdminTicketActions 
              ticket={ticket}
              onAction={handleAdminAction}
              loading={loading}
              onRefresh={fetchTicket}
              userContext={userPermissions} // FIXED: Pass user context from backend
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketDetail;